import { useMemo, useState } from 'react'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import {
  LBDialog,
  LBDialogContent,
  LBDialogHeader,
  LBDialogTitle,
  LBDialogDescription,
  LBDialogFooter,
  LBButton,
  LBAlert,
  LBAlertDescription,
  LBInfoCircleIcon,
  lbToast
} from '@linearb/buzz'
import type { LBDateRange } from '@linearb/buzz'
import DateRangePickerWithPresets from '../../custom-components/DateRangePickerWithPresets'
import {
  getDateRangePresets,
  getDefaultBackfillRange,
  MAX_BACKFILL_SPAN_DAYS
} from '../../custom-components/dateRangePresets'
import type { DateRangePreset } from '../../custom-components/dateRangePresets'
import {
  getAiMethodLabel,
  AiProviderLabel
} from '../../custom-components/SensorsIcons'
import { inclusiveDays } from '../../utils/dateHelpers'
import { useTriggerAiBackfill } from '../../hooks/queries/useOrganizations'
import { useAiBackfill } from '../../hooks/useAiBackfill'
import { useSfdcAccount } from '../../hooks/queries/useSfdc'
import { trackAction } from '../../lib/datadogRum'
import type { OrgAiIntegration } from '../../types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string | number
  organizationName?: string
  integration: OrgAiIntegration | null
}

// A 90-day window ending `chunkIndex * 90` days before yesterday. chunkIndex 0 is
// the last 90 days ("Past 3 months", already provided by getDateRangePresets); the
// earlier chunks let an operator step back through history in 3-month slices, since
// a single backfill is capped at 90 days.
const backfillChunk = (chunkIndex: number, label: string): DateRangePreset => ({
  label,
  getRange: (now = dayjs()) => {
    const to = now
      .subtract(1, 'day')
      .subtract(chunkIndex * MAX_BACKFILL_SPAN_DAYS, 'day')
    return {
      from: to.subtract(MAX_BACKFILL_SPAN_DAYS - 1, 'day').toDate(),
      to: to.toDate()
    }
  }
})

// getDateRangePresets()'s generic presets (Current month, Past 30 days, …) can
// return a `to` of today or later; the daily run already covers today, so cap
// every backfill preset at yesterday.
const ROLLING_PRESET_LABELS = new Set(['Past 30 days'])

const capToYesterday = (preset: DateRangePreset): DateRangePreset => ({
  label: preset.label,
  getRange: (now = dayjs()) => {
    const { from, to } = preset.getRange(now)
    const yesterday = now.subtract(1, 'day')
    // Rolling windows always end today; shift the whole window back a day
    // instead of just trimming `to`, so excluding today doesn't shrink its span.
    if (ROLLING_PRESET_LABELS.has(preset.label)) {
      return { from: dayjs(from).subtract(1, 'day').toDate(), to: yesterday.toDate() }
    }
    const cappedTo = dayjs(to).isAfter(yesterday, 'day') ? yesterday.toDate() : to
    return {
      from: dayjs(from).isAfter(cappedTo, 'day') ? cappedTo : from,
      to: cappedTo
    }
  }
})

// Standard presets minus the >90d ones (Past 6 months + quarters), with Past 3 months
// as the exact 90-day chunk so it survives clampRange, plus the earlier 3-month chunks.
// Filtered by label, not a positional slice, since the quarter count varies by date.
const BACKFILL_PRESETS: DateRangePreset[] = [
  ...getDateRangePresets()
    .filter(p => p.label !== 'Past 6 months' && !/^Q[1-4]\b/.test(p.label))
    .map(p =>
      p.label === 'Past 3 months' ? backfillChunk(0, p.label) : capToYesterday(p)
    ),
  backfillChunk(1, '3–6 months ago'),
  backfillChunk(2, '6–9 months ago'),
  backfillChunk(3, '9–12 months ago')
]

// The preset whose window, evaluated on the day the backfill started, equals since..until.
export const matchBackfillPreset = (
  since: string,
  until: string,
  ranAt: Dayjs
): string | null =>
  BACKFILL_PRESETS.find(p => {
    const { from, to } = p.getRange(ranAt)
    return (
      dayjs(from).format('YYYY-MM-DD') === since &&
      dayjs(to).format('YYYY-MM-DD') === until
    )
  })?.label ?? null

const clampRange = (r: LBDateRange | undefined): LBDateRange | undefined => {
  if (!r?.from || !r?.to) {return r}
  const today = dayjs()
  let to = dayjs(r.to)
  if (to.isAfter(today, 'day')) {to = today}
  let from = dayjs(r.from)
  const maxFrom = to.subtract(MAX_BACKFILL_SPAN_DAYS - 1, 'day')
  if (from.isBefore(maxFrom, 'day')) {from = maxFrom}
  if (from.isAfter(to, 'day')) {from = to}
  return { from: from.toDate(), to: to.toDate() }
}

const AiBackfillDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  organizationId,
  organizationName,
  integration
}) => {
  const { data: sfdcAccount } = useSfdcAccount(organizationId)
  const [range, setRange] = useState<LBDateRange | undefined>(
    getDefaultBackfillRange()
  )
  const saId = integration?.sensor_authorization_id ?? null
  // provider = badge label; method + connected date carry to the dock/drawer.
  const provider = integration?.tool
    ? integration.tool.charAt(0).toUpperCase() + integration.tool.slice(1)
    : 'AI'
  const method = integration ? getAiMethodLabel(integration) : null
  const since = range?.from ? dayjs(range.from).format('YYYY-MM-DD') : ''
  const until = range?.to ? dayjs(range.to).format('YYYY-MM-DD') : ''

  const stateOk = integration?.state === 'ok'
  const isOtel = integration?.collection_method === 'otel'

  // clampRange guarantees a valid window, so the only error is "nothing picked".
  const windowError = !since || !until ? 'Pick a date range.' : null

  // Rough pre-fire estimate (~1 min/30d, up to ~4x for enterprise/multi-org);
  // the precise row-volume estimate shows in the dock after firing.
  const estimate = useMemo(() => {
    if (!since || !until) {return null}
    const days = inclusiveDays(since, until)
    const low = Math.max(1, Math.round(days / 30))
    const high = Math.min(15, Math.max(low, Math.round((days / 30) * 4)))
    return low === high ? `~${low} min` : `~${low}–${high} min`
  }, [since, until])

  const triggerMutation = useTriggerAiBackfill(organizationId, saId)
  const { start } = useAiBackfill()

  const handleStart = (): void => {
    if (!saId || windowError) {return}
    triggerMutation.mutate(
      { since, until },
      {
        onSuccess: trigger => {
          trackAction('ai_backfill_started', {
            tool: integration?.tool,
            sensor_authorization_id: saId,
            since,
            until
          })
          start({
            trigger,
            orgId: organizationId,
            orgName: organizationName,
            orgWebsite: sfdcAccount?.website,
            saId,
            tool: provider,
            method: method ?? undefined,
            connectedAt: integration?.created_at,
            since,
            until
          })
          onOpenChange(false)
          const preset = matchBackfillPreset(since, until, dayjs())
          lbToast({
            title: 'Backfill started',
            description: `${provider} ${preset ? `${preset} · ` : ''}${since} → ${until} (est. ${trigger.estimate.label}). Running in the background.`,
            variant: 'success'
          })
        },
        onError: (err: Error) => {
          lbToast({
            title: 'Could not start backfill',
            description: err?.message || 'Unexpected error',
            variant: 'destructive'
          })
        }
      }
    )
  }

  return (
    <LBDialog open={open} onOpenChange={onOpenChange}>
      <LBDialogContent className='sm:max-w-md'>
        <LBDialogHeader>
          <LBDialogTitle className='flex flex-wrap items-center gap-2'>
            Backfill AI metrics
            <AiProviderLabel tool={integration?.tool} method={method} />
          </LBDialogTitle>
          <LBDialogDescription>
            {isOtel
              ? 'Recalculate cost, models and tokens for a past window from the telemetry we already stored (runs in the background).'
              : 'Re-collect historical cost, models and tokens for this integration over a past window (runs in the background).'}
          </LBDialogDescription>
        </LBDialogHeader>

        <div className='flex flex-col gap-4 py-2'>
          {isOtel && (
            <LBAlert variant='attention'>
              <LBAlertDescription className='flex items-start gap-2'>
                <LBInfoCircleIcon className='mt-0.5 h-4 w-4 shrink-0' />
                <span>
                  OpenTelemetry has no history to fetch. Usage only reaches us
                  while developers are coding, streamed live from their machines,
                  and there is no API to ask for past days. This just
                  recalculates the metrics from telemetry we already stored; days
                  nobody reported stay empty.
                </span>
              </LBAlertDescription>
            </LBAlert>
          )}

          {!stateOk && (
            <LBAlert variant='attention'>
              <LBAlertDescription>
                This integration is{' '}
                <strong>{integration?.state ?? 'not ok'}</strong> and must be
                re-authorized before it can be backfilled.
              </LBAlertDescription>
            </LBAlert>
          )}

          <div className='flex flex-col gap-2'>
            <span className='text-xs text-muted-foreground'>
              Window (inclusive, max {MAX_BACKFILL_SPAN_DAYS} days; presets
              end yesterday)
            </span>
            <DateRangePickerWithPresets
              selected={range}
              onChange={r => setRange(clampRange(r))}
              presets={BACKFILL_PRESETS}
              placeholder='Pick a date range'
            />
            {windowError && (
              <span className='text-xs text-red-600'>{windowError}</span>
            )}
            {estimate && (
              <span className='text-xs text-muted-foreground'>
                Estimated time:{' '}
                <span className='font-medium text-foreground'>{estimate}</span>{' '}
                (longer for enterprise / multi-org)
              </span>
            )}
            <span className='text-xs text-muted-foreground'>
              Runs in the background — you can close this and keep working.
            </span>
          </div>
        </div>

        <LBDialogFooter>
          <LBButton variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </LBButton>
          <LBButton
            onClick={handleStart}
            disabled={!stateOk || !!windowError || triggerMutation.isPending}
          >
            {triggerMutation.isPending ? 'Starting…' : 'Start backfill'}
          </LBButton>
        </LBDialogFooter>
      </LBDialogContent>
    </LBDialog>
  )
}

export default AiBackfillDialog
